import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeTransformSnapshot } from '@/lib/groups/shape-group-tree'
import { buildUnifiedSelectionSingleShapeResizeUndo } from '@/lib/transforms/unified-selection-resize'
import type { InteractionState } from '../types/interaction-state'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'

/** Undo metadata for a single-shape move, resize or rotate commit (undefined when nothing moved). */
export function buildSingleShapeCommitUndo(
  interaction: InteractionState,
  committedShape: ShapeData
): ShapeCommittedUndoMeta | undefined {
  const EPS_POS = 1e-4
  let commitUndo: ShapeCommittedUndoMeta | undefined

  if (interaction.type === 'rotate') {
    commitUndo = {
      before: { rotation: interaction.initial.rotation ?? 0 },
      after: { rotation: committedShape.rotation ?? 0 },
    }
  } else if (
    interaction.type === 'move' &&
    !interaction.multiMoveInitials &&
    (Math.abs(interaction.initial.x - committedShape.x) > EPS_POS ||
      Math.abs(interaction.initial.y - committedShape.y) > EPS_POS)
  ) {
    commitUndo = {
      before: { x: interaction.initial.x, y: interaction.initial.y },
      after: { x: committedShape.x, y: committedShape.y },
    }
  } else if (interaction.type === 'resize' && interaction.direction) {
    const init = interaction.initial

    commitUndo = buildUnifiedSelectionSingleShapeResizeUndo({
      before: {
        x: init.x,
        y: init.y,
        width: init.width,
        height: init.height,
        path: init.path,
        pathCommands: init.pathCommands,
      },
      after: {
        x: committedShape.x,
        y: committedShape.y,
        width: committedShape.width,
        height: committedShape.height,
        path: committedShape.path,
        pathCommands: committedShape.pathCommands,
      },
      mode: 'any',
      includePathWhenPresent: true,
      positionEpsilon: EPS_POS,
      sizeEpsilon: 1e-4,
    })
  }

  return commitUndo
}

/**
 * Batch items for a gesture on a single selected group: the group and every descendant whose
 * position, size or rotation changed from its gesture-start snapshot.
 */
export function buildGroupTransformCommitItems(
  groupTransformInitials: Record<string, ShapeTransformSnapshot>,
  batchSource: InteractionState['type'],
  shapes: ShapeData[]
): BatchShapeTransformItem[] {
  const EPS = 1e-4
  const EPS_ROT = 1e-2
  const items: BatchShapeTransformItem[] = []

  for (const [shapeId, before] of Object.entries(groupTransformInitials)) {
    const shape = shapes.find((candidate) => candidate.id === shapeId)

    if (!shape) continue

    const afterRotation = shape.rotation ?? 0
    const beforeRotation = before.rotation
    const changedPosition = Math.abs(shape.x - before.x) > EPS || Math.abs(shape.y - before.y) > EPS
    const changedSize =
      Math.abs(shape.width - before.width) > EPS || Math.abs(shape.height - before.height) > EPS
    const changedRotation = Math.abs(afterRotation - beforeRotation) > EPS_ROT

    if (!(changedPosition || changedSize || changedRotation)) continue

    if (batchSource === 'move') {
      items.push({
        shapeId,
        shape,
        before: { x: before.x, y: before.y },
        after: { x: shape.x, y: shape.y },
      })
      continue
    }

    if (batchSource === 'rotate') {
      items.push({
        shapeId,
        shape,
        before: { x: before.x, y: before.y, rotation: before.rotation },
        after: { x: shape.x, y: shape.y, rotation: shape.rotation ?? 0 },
      })
      continue
    }

    items.push({
      shapeId,
      shape,
      before: {
        x: before.x,
        y: before.y,
        width: before.width,
        height: before.height,
        rotation: before.rotation,
      },
      after: {
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
        rotation: shape.rotation ?? 0,
      },
    })
  }

  return items
}
